import React, { useCallback, useMemo } from "react";
import { Divider, Grid, Stack, Tooltip } from "@mui/material";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import _ from "lodash";

import Avatar from "../../../../components/Avatar";
import {
  createAvtarName,
  fixedDigitsAfterDecimal,
  hasImage,
} from "../../../../utils";
import { useSetMultipleParams } from "../../../../hooks/useSetMultipleParams";
import lateIcon from "../../../../styles/svg/warning-icon.svg";
import { useResponsive } from "../../../../hooks/useResponsive";
import { ByTaskAvatar } from "./ByTaskAvatar";
import { ByTaskMobileProgressBar } from "./ByTaskMobileProgressBar";
import { ByTaskDeskTopProgressBar } from "./ByTaskDeskTopProgressBar";

function ByTaskTab({ worklogs }) {
  const [setMultipleSearchParams] = useSetMultipleParams();
  const navigate = useNavigate();
  const isMobile = useResponsive("down", "md");
  const openBoard = useCallback(
    (boardId) => {
      navigate(`/board/${boardId}`);
    },
    [navigate]
  );

  const isSameDay = useCallback((createDate, dateTime) => {
    const localCreatedAt = moment.utc(createDate)?.local();
    const localDateTime = moment.utc(dateTime)?.local();

    return localCreatedAt?.isSame(localDateTime, "day");
  }, []);

  const handleOpenCardDetail = useCallback(
    (boardId, cardId) => {
      setMultipleSearchParams([
        {
          name: "brd_id",
          value: boardId,
        },
        {
          name: "card_id",
          value: cardId,
        },
      ]);
    },
    [setMultipleSearchParams]
  );

  const worklogsByBoard = useMemo(() => {
    return _.groupBy(worklogs, (worklog) => worklog.cardlog.card.card_id);
  }, [worklogs]);

  return (
    <>
      {Object.entries(worklogsByBoard)?.map(([id, card]) => (
        <Grid
          key={`worklog-by-task-${id}`}
          container
          spacing={2}
          className="worklog-item d-flex justify-content-space-between">
          {isMobile ? (
            <ByTaskAvatar
              card={card}
              openBoard={openBoard}
              handleOpenCardDetail={handleOpenCardDetail}
            />
          ) : (
            <Grid item xs={12} md={9}>
              <div className="d-flex w-100">
                <Avatar
                  username={`${card[0]?.cardlog?.user?.first_name} ${card[0]?.cardlog?.user?.last_name}`}
                  user_image={card[0]?.cardlog?.user?.user_image}
                  title={`${card[0]?.cardlog?.user?.first_name} ${card[0]?.cardlog?.user?.last_name}`}
                  className="user-avtar-dropdown user-avatar mr-8 cursor-pointer"
                  onClick={() => {
                    navigate(
                      `/my-task?tab=team&uid=${card[0]?.cardlog?.user?.user_id}`
                    );
                  }}
                  style={
                    !hasImage(card[0]?.cardlog?.user?.user_image) && {
                      bgcolor:
                        card[0]?.cardlog?.user?.profile_color?.background,
                      color: card[0]?.cardlog?.user?.profile_color?.text,
                    }
                  }>
                  {createAvtarName(
                    `${card[0]?.cardlog?.user?.first_name} ${card[0]?.cardlog?.user?.last_name}` ||
                      ""
                  )}
                </Avatar>
                <div className="w-100">
                  <div className="d-flex justify-content-space-between align-items-center">
                    <div className="d-flex align-items-baseline">
                      <Tooltip title={card[0]?.board_name}>
                        <a
                          className="board-anquor cursor-pointer"
                          onClick={() =>
                            openBoard(card[0]?.cardlog?.card?.board_id)
                          }>
                          {`${card[0]?.key}`?.toUpperCase()}
                        </a>
                      </Tooltip>
                      -
                      <a
                        className="board-anquor cursor-pointer mr-1"
                        onClick={() =>
                          handleOpenCardDetail(
                            card[0]?.cardlog?.card?.board_id,
                            card[0]?.cardlog?.card?.card_id
                          )
                        }>
                        {card[0]?.cardlog?.card?.card_id}:
                      </a>
                      <p className="worklog-title mr-2">
                        {card[0]?.cardlog?.card?.title}
                      </p>
                      {card[0]?.epic_name && (
                        <div className="card-epic-tag mt-0">
                          <p className="card-epic-tag-text">
                            {card[0]?.epic_name}
                          </p>
                        </div>
                      )}
                    </div>
                    <p className="hours min-width-60 align-self-base ml-1">
                      {fixedDigitsAfterDecimal(
                        card.reduce(
                          (sum, cardDetails) =>
                            sum + cardDetails?.cardlog?.hours_spent,
                          0
                        )
                      )}
                      Hour
                    </p>
                  </div>
                  {card.map((cardDetails, index) => (
                    <div
                      key={`worklog-by-task-tab-${index}`}
                      className={index === 0 ? "mt-2" : "mt-11"}>
                      <Stack direction={"row"}>
                        <p
                          className="worklog-date-time"
                          style={{ justifyContent: "left" }}>
                          {!isSameDay(
                            cardDetails?.cardlog?.created_at,
                            cardDetails?.cardlog?.date_time
                          ) && (
                            <img
                              src={lateIcon}
                              className="late-icon"
                              alt="late-icon"
                            />
                          )}
                          {moment
                            .utc(cardDetails?.cardlog?.date_time)
                            .local()
                            .format("MMMM D, YYYY [at] h:mm A")}
                        </p>

                        <Divider
                          orientation="vertical"
                          variant="middle"
                          flexItem
                          className="ml-1 mr-6 mt-0 mb-0"
                          sx={{ height: "14px", width: "2px" }}
                        />
                        <p className="worklog-date-time">
                          {`${
                            cardDetails?.cardlog?.hours_spent == 1
                              ? `${cardDetails?.cardlog?.hours_spent}Hour`
                              : `${cardDetails?.cardlog?.hours_spent} Hours`
                          }`}
                        </p>
                      </Stack>
                      <p
                        className="worklog-worklog mt-0"
                        dangerouslySetInnerHTML={{
                          __html: cardDetails?.cardlog?.work_performed,
                        }}></p>
                    </div>
                  ))}
                </div>
              </div>
            </Grid>
          )}

          {isMobile ? (
            <>
              <ByTaskMobileProgressBar card={card} isSameDay={isSameDay} />
            </>
          ) : (
            <ByTaskDeskTopProgressBar card={card} />
          )}
        </Grid>
      ))}
    </>
  );
}

export default ByTaskTab;
